import { TenantInfo } from '~/containers/RentRoll/TenantRentRollTable';
import { emperor } from '~/services';
import { BaseEmperorResponse } from '~/types/common';
import { CreateResidentBodyType } from '~/types/residentManagement';

export type SendReviewInvitationApiPayload = {
  user: string;
  sendInvitationSms?: boolean;
};

export const getResidents = async (buildingId: string) => {
  return await emperor.get(`v2/building/${buildingId}/all-users`);
};

export const fetchResidentsMinData = async (buildingId: string) => {
  return await emperor.get(`/building/${buildingId}/attribution-users`);
};

export const createResident = async (residentBody: CreateResidentBodyType) => {
  return await emperor.post(
    `user/verified/${residentBody.building}`,
    residentBody
  );
};

export type ImportResidentRow = Record<string, string>;

export type ImportResidentResult = {
  row: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  unit: string;
  status: 'create' | 'skip' | 'created' | 'failed';
  reason: string | null;
};

export type ImportResidentsResponse = {
  dryRun: boolean;
  summary: {
    total: number;
    toCreate: number;
    created: number;
    failed: number;
    skipped: number;
  };
  results: ImportResidentResult[];
};

export const importResidents = async (
  buildingId: string,
  rows: ImportResidentRow[],
  dryRun: boolean
) => {
  return await emperor.post<ImportResidentsResponse>(
    `/building/${buildingId}/residents/import`,
    { rows, dryRun }
  );
};

export const deactivateResident = async (residentId: string) => {
  return await emperor.post(`/user/${residentId}/deactivate`);
};

export const verifyResidentPhone = async (residentId: string) => {
  return await emperor.post(`/user/${residentId}/verify-phone`);
};

export const deactivateMultipleResidents = async (residentIds: string[]) => {
  return await emperor.post(`/user/deactivate-residents`, {
    users: JSON.stringify(residentIds)
  });
};

export const removeFromGoogleReviewOutreach = async (residentId: string) => {
  return await emperor.post(`/user/remove-from-review-outreach`, {
    userId: residentId
  });
};

export const activateResident = async (residentId: string) => {
  return await emperor.post(`/user/${residentId}/activate`);
};

export const activateMultipleResidents = async (residentIds: string[]) => {
  return await emperor.post(`/user/activate-residents`, {
    users: JSON.stringify(residentIds)
  });
};

export const sendWelcomeMail = async (residentIds: string[]) => {
  return await emperor.post(`/user/send-welcome-mail`, {
    users: JSON.stringify(residentIds)
  });
};

export const sendReviewInvitation = async (
  payload: SendReviewInvitationApiPayload
): Promise<BaseEmperorResponse<any>> => {
  return emperor.post(`/invitation`, payload);
};

export const getResidentProfile = async (residentId: string) => {
  return await emperor.get(`/user/${residentId}/resident-profile`);
};

export type RestrictResidentPayload = {
  reason?: string;
  subject?: string;
  contents?: string;
  skipEmail?: boolean;
};

export const restrictResident = async (
  residentId: string,
  payload: RestrictResidentPayload = {}
) => {
  return await emperor.post(`/user/${residentId}/restrict`, payload);
};

export const unrestrictResident = async (residentId: string) => {
  return await emperor.post(`/user/${residentId}/unrestrict`);
};

export const updateResidentProfile = async (
  residentBody: CreateResidentBodyType
) => {
  return await emperor.patch(`/user/${residentBody.id}`, residentBody);
};

export const removeProfilePic = async (residentId: string) => {
  return await emperor.patch(`/user/${residentId}/remove-profile-pic`);
};

export const updateNotifcationSettings = async (
  residentId: string,
  uuids: string[]
) => {
  return await emperor.patch(
    `/user/${residentId}/update-notification-settings`,
    {
      uuids: JSON.stringify(uuids)
    }
  );
};

export const updatePassword = async (userId?: string, password?: string) => {
  return await emperor.patch('/user/update-password', { password, userId });
};

export const uploadAndParseCsvForRentRoll = async (payload: FormData) => {
  return await emperor.post('/resident-management/upload-rent-roll', payload);
};

export const uploadRentRollForPM = async (payload: FormData) => {
  return await emperor.post(
    '/resident-management/upload-pm-rent-roll',
    payload
  );
};

export const previewRentRoll = async ({
  file,
  tenants,
  buildingId,
  buildingName
}: {
  file: File;
  tenants: TenantInfo[];
  buildingId?: string;
  buildingName?: string;
}) => {
  const form = new FormData();
  form.append('tenants', JSON.stringify(tenants));
  if (buildingId) {
    form.append('buildingId', buildingId);
  }
  if (buildingName) {
    form.append('buildingName', buildingName);
  }
  form.append('file', file, file.name);
  return await emperor.post('/resident-management/rent-roll/preview', form);
};

export const confirmRentRollImport = async (previewId: string) => {
  return await emperor.post('/resident-management/rent-roll/confirm', {
    previewId
  });
};

export const listRentRollImports = async (buildingId: string) => {
  return await emperor.get('/resident-management/rent-roll/list', {
    params: { buildingId }
  });
};

export const bulkMoveOutTenants = async (payload: {
  building: string;
  moveOuts: TenantInfo[];
}) => {
  return await emperor.post(
    '/resident-management/bulk-moveout-tenants',
    payload
  );
};
